import { useEffect, useLayoutEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { api, type CardView, type CardVersion, type Evidence, type EvidenceView, type NewsPayload } from '../api';
import { isCrossFitPayload, sourceNames } from '../briefing';
import { classifyUnknowns, distinctSources, groupEvidence, type CoverageNote } from '../coverage';
import { formatDateTime, relativeAge } from '../format';
import { CrossFitView } from './crossfit';
import { EventDetails } from './events/EventDetails';
import { isEventPayload, occurrenceForVersion } from '../eventsApi';
import { ChangesPanel } from './changes';
import { EDITION_PARAM, peekOpener, useCloseDetail, useOpenDetail } from './detail-link';
import { InterestButtons, QualityForm } from './feedback';
import { FollowButton, SubjectChips } from './follow';
import { ARTICLE_TYPE_LABELS, dataRetrieval, dayOnly, recordKind, useSourceCatalog } from './sources';
import { Chip, ErrorState, Loading, Notice, useAsync } from './ui';

/**
 * Details / Belege: the one detail system for every card.
 *
 * Full headline and summary, key points, open questions and each source's own
 * block with its original link and times come first; exact version and
 * processing details sit in a disclosure. Feedback binds to the version shown.
 *
 * Keyboard: focus moves into the dialog, Tab stays inside it, Escape closes it,
 * and focus returns to the element that opened it. The page behind is inert.
 */

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

export function DetailModal({ versionId }: { versionId: string }) {
  const close = useCloseDetail();
  const dialogRef = useRef<HTMLDivElement>(null);
  // Captured at the first render; switching versions inside the dialog keeps it.
  const opener = useRef<HTMLElement | null>(peekOpener());

  const card = useAsync(() => api.cardVersion(versionId), [versionId]);
  const evidence = useAsync(() => api.cardEvidence(versionId), [versionId]);
  const cardId = card.data?.card.id;
  const versions = useAsync(async () => (cardId ? api.cardVersions(cardId) : null), [cardId]);

  // Capture the opener once, and give focus back to it on close.
  useLayoutEffect(() => {
    const shell = document.getElementById('app-shell');
    shell?.setAttribute('inert', '');
    shell?.setAttribute('aria-hidden', 'true');
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      shell?.removeAttribute('inert');
      shell?.removeAttribute('aria-hidden');
      document.body.style.overflow = prevOverflow;
      const target = opener.current;
      if (target && target.isConnected) target.focus();
      else document.getElementById('content')?.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const items = [...dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true',
    );
    if (items.length === 0) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === dialogRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        ref={dialogRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="detail-title"
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="modal-top">
          <span className="eyebrow">Hintergrund &amp; Quellen</span>
          <button type="button" className="small-button" onClick={close} aria-label="Details schliessen">
            Schliessen ×
          </button>
        </div>
        <div className="modal-body">
          {card.loading && !card.data && (
            <>
              <h2 id="detail-title" className="visually-hidden">
                Details werden geladen
              </h2>
              <Loading />
            </>
          )}
          {card.error && (
            <>
              <h2 id="detail-title" className="visually-hidden">
                Details nicht verfügbar
              </h2>
              <ErrorState error={card.error} onRetry={card.reload} />
            </>
          )}
          {card.data && (
            <DetailContent
              view={card.data}
              evidence={evidence}
              versions={versions.data?.versions ?? null}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DetailContent({
  view,
  evidence,
  versions,
}: {
  view: CardView;
  evidence: { data: EvidenceView | null; error: Error | null; loading: boolean };
  versions: CardVersion[] | null;
}) {
  const v = view.version;
  const openVersion = useOpenDetail();
  const latest = versions && versions.length > 0 ? versions.reduce((a, b) => (b.version > a.version ? b : a)) : null;
  const isLatest = !latest || latest.id === v.id;
  const catalog = useSourceCatalog();
  const [params] = useSearchParams();
  const edition = params.get(EDITION_PARAM);
  // Items without a stated source name fall back to the catalogue name, never the bare id.
  const names = sourceNames(view).map((n) => catalog.get(n)?.name ?? n);

  return (
    <article>
      <div className="meta">
        {view.topic && <span className="tag">{view.topic.label}</span>}
        {names.length > 0 && <span>{names.join(' · ')}</span>}
        <span>Version {v.version} · erstellt {relativeAge(v.created_at)}</span>
      </div>
      <h2 id="detail-title" className="story-title" lang={v.language || undefined}>
        {v.title}
      </h2>

      {!isLatest && latest && (
        <Notice kind="info" title="Du siehst eine frühere Version">
          Diese Version {v.version} war damals veröffentlicht; seither gibt es Version {latest.version}.{' '}
          <button type="button" className="text-button" onClick={(e) => openVersion(latest.id, e.currentTarget)}>
            Neueste Version ansehen
          </button>
        </Notice>
      )}

      <SubjectChips view={view} />
      <ChangesPanel versionId={v.id} edition={edition} />

      {isCrossFitPayload(v.payload) ? (
        <CrossFitView p={v.payload} headingLevel={3} />
      ) : isEventPayload(v.payload) ? (
        <EventDetails payload={v.payload} occurrence={occurrenceForVersion(v.id)} />
      ) : (
        <NewsDetail view={view} payload={v.payload as NewsPayload} evidence={evidence} />
      )}

      <div className="actions modal-actions">
        <FollowButton view={view} variant="button" />
        <InterestButtons key={`i-${v.id}`} view={view} briefingId={edition} />
      </div>

      {view.card.section === 'news' && (
        <QualityForm key={v.id} versionId={v.id} versionNumber={v.version} existing={view.feedback ?? null} briefingId={edition} />
      )}

      <details className="disclosure">
        <summary>Version &amp; Verarbeitung</summary>
        <ProcessingDetails view={view} evidence={evidence.data} versions={versions} />
      </details>
    </article>
  );
}

function NewsDetail({
  view,
  payload,
  evidence,
}: {
  view: CardView;
  payload: NewsPayload;
  evidence: { data: EvidenceView | null; error: Error | null; loading: boolean };
}) {
  const v = view.version;
  const raw = evidence.data?.evidence ?? v.evidence ?? [];
  const revisions = evidence.data?.revisions ?? [];
  const fetched = (id: string) => revisions.find((r) => r.revision_id === id)?.fetched_at;
  const items = groupEvidence(raw, fetched);
  const { coverage, questions } = classifyUnknowns(payload);
  // A single-source card has nothing to contrast: its "additions" are the story itself.
  const multiSource = distinctSources(raw) > 1;
  const additions = new Map<string, string[]>();
  if (multiSource) {
    for (const a of payload.source_additions ?? []) {
      const list = additions.get(a.source_id) ?? [];
      list.push(a.addition);
      additions.set(a.source_id, list);
    }
  }
  const coverageBy = new Map<string, CoverageNote[]>();
  for (const c of coverage) {
    const list = coverageBy.get(c.source_id) ?? [];
    list.push(c);
    coverageBy.set(c.source_id, list);
  }
  const shownFor = new Set<string>();
  const extraSources = [...new Set([...additions.keys(), ...coverageBy.keys()])].filter((sid) => !raw.some((e) => e.source_id === sid));

  return (
    <>
      {v.degraded && (
        <Notice kind="warn" title="Ohne Sprachmodell erstellt">
          Die lokale Inferenz war nicht verfügbar. Der Text unten ist Quellentext, keine Zusammenfassung. Es wurde nichts erfunden.
        </Notice>
      )}
      {v.collection_status === 'partial' && (
        <Notice kind="warn" title="Unvollständig erfasst">
          Nicht alle Quellentexte lagen vollständig vor; Details stehen bei den Quellen.
        </Notice>
      )}
      <p className="lead" lang={v.language || undefined}>
        {payload.summary || v.summary}
      </p>

      {payload.key_points && payload.key_points.length > 0 && (
        <>
          <h3>Das Wesentliche</h3>
          <ul className="points">
            {payload.key_points.map((k, i) => (
              <li key={i}>{k}</li>
            ))}
          </ul>
        </>
      )}

      {questions.length > 0 && (
        <div className="notice notice-open">
          <strong>Offene Fragen</strong>
          <ul>
            {questions.map((u, i) => (
              <li key={i}>{u}</li>
            ))}
          </ul>
        </div>
      )}

      <h3>{multiSource ? 'Quellen nebeneinander' : 'Quelle'}</h3>
      {evidence.loading && !evidence.data && <Loading label="Belege werden geladen…" />}
      {evidence.error && <Notice kind="warn">Die Belege konnten nicht vollständig geladen werden: {evidence.error.message}</Notice>}
      {items.length === 0 && !evidence.loading && <p className="muted">Keine Quellen hinterlegt.</p>}
      {items.map(({ latest: e }) => {
        const rev = revisions.find((r) => r.revision_id === e.revision_id);
        const first = !shownFor.has(e.source_id);
        shownFor.add(e.source_id);
        const adds = first ? additions.get(e.source_id) : undefined;
        const notes = first ? coverageBy.get(e.source_id) : undefined;
        return (
          <section className="source-entry" key={e.item_id || e.revision_id} aria-label={`Quelle ${e.source_name || e.source_id}`}>
            <div className="spread">
              <strong>
                {e.source_name || e.source_id}
                <RoleTag e={e} />
              </strong>
              <Chip kind={e.text_source === 'full_text' ? 'ok' : 'warn'}>
                {e.text_source === 'full_text' ? 'Volltext gelesen' : e.text_source === 'excerpt' ? 'nur Anriss' : 'nur Titel'}
              </Chip>
            </div>
            <p className="source-title">
              <a href={e.url} target="_blank" rel="noopener noreferrer">
                {e.title}
                <span className="visually-hidden"> (Originalartikel, öffnet in neuem Tab)</span> ↗
              </a>
            </p>
            <p className="muted small">
              {e.published_at ? `Veröffentlicht ${formatDateTime(e.published_at)}` : 'Veröffentlichungszeit unbekannt'}
              {rev && ` · abgerufen ${formatDateTime(rev.fetched_at)}`}
            </p>
            <AttributionLines e={e} payload={payload} />
            {notes?.map((c, i) => (
              <p className="coverage-note" key={i}>
                {c.note}
              </p>
            ))}
            {adds && (
              <div className="contribution">
                <span className="mini-h">Beitrag dieser Quelle</span>
                <ul>
                  {adds.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </div>
            )}
            {rev && !rev.completeness.complete && (
              <p className="warn-text small">
                {rev.completeness.missing_fields && rev.completeness.missing_fields.length > 0
                  ? `Unvollständiger Abruf, fehlend: ${rev.completeness.missing_fields.join(', ')}.`
                  : 'Der Abruf war unvollständig.'}
              </p>
            )}
          </section>
        );
      })}
      {/* Contributions or coverage notes whose source is not among the evidence are still shown. */}
      {extraSources.map((sid) => (
        <section className="source-entry" key={`extra-${sid}`}>
          <strong>{sid}</strong>
          {coverageBy.get(sid)?.map((c, i) => (
            <p className="coverage-note" key={i}>
              {c.note}
            </p>
          ))}
          {additions.get(sid) && (
            <div className="contribution">
              <span className="mini-h">Beitrag dieser Quelle</span>
              <ul>
                {additions.get(sid)!.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      ))}
    </>
  );
}

function ProcessingDetails({
  view,
  evidence,
  versions,
}: {
  view: CardView;
  evidence: EvidenceView | null;
  versions: CardVersion[] | null;
}) {
  const v = view.version;
  const openVersion = useOpenDetail();
  return (
    <div className="processing">
      <dl className="kv">
        <dt>Version</dt>
        <dd>
          {v.version} · {v.id}
        </dd>
        <dt>Erstellt</dt>
        <dd>{formatDateTime(v.created_at)}</dd>
        <dt>Stand der Inhalte</dt>
        <dd>{formatDateTime(v.freshness_at)}</dd>
        <dt>Erfassung</dt>
        <dd>{v.collection_status}</dd>
        <dt>Erzeuger</dt>
        <dd>
          {v.producer} {v.producer_version}
        </dd>
        <dt>Schema</dt>
        <dd>{v.payload_schema}</dd>
        <dt>Inhalts-Hash</dt>
        <dd>{v.content_hash}</dd>
        {view.entry && (
          <>
            <dt>Rang in der Ausgabe</dt>
            <dd>
              {view.entry.position}
              {view.entry.in_more ? ' (weitere Meldungen)' : ' (Titelseite)'}
            </dd>
          </>
        )}
      </dl>
      {v.payload.payload_type === 'news.v1' && <CoverageDetails payload={v.payload as NewsPayload} evidence={evidence} fallback={v.evidence} />}
      {evidence?.deterministic && (
        <p className="muted small">Deterministisch aus strukturierten Daten erzeugt; kein Sprachmodell beteiligt.</p>
      )}
      {evidence?.model_run && (
        <>
          <h4 className="mini-h">Modelllauf</h4>
          <dl className="kv">
            <dt>Status</dt>
            <dd>{evidence.model_run.status}</dd>
            <dt>Anbieter / Modell</dt>
            <dd>
              {evidence.model_run.provider} / {evidence.model_run.model}
            </dd>
            {evidence.model_run.runtime && (
              <>
                <dt>Runtime</dt>
                <dd>{evidence.model_run.runtime}</dd>
              </>
            )}
            <dt>Prompt / Schema</dt>
            <dd>
              {evidence.model_run.prompt_version} / {evidence.model_run.schema_version}
            </dd>
            {evidence.model_run.latency_ms != null && (
              <>
                <dt>Dauer</dt>
                <dd>{evidence.model_run.latency_ms} ms</dd>
              </>
            )}
          </dl>
          {evidence.model_run.error && <p className="warn-text small">Fehler des Modelllaufs: {evidence.model_run.error}</p>}
        </>
      )}
      {evidence?.revisions?.some((r) => r.raw_artifact) && (
        <>
          <h4 className="mini-h">Rohdaten</h4>
          <ul className="plain small">
            {evidence.revisions
              .filter((r) => r.raw_artifact)
              .map((r) => (
                <li key={r.revision_id}>
                  {r.source_id}: {r.raw_artifact!.media_type}, {Math.round(r.raw_artifact!.size_bytes / 1024)} kB ·{' '}
                  {r.raw_artifact!.state === 'present'
                    ? `aufbewahrt${r.raw_artifact!.expires_at ? ` bis ${formatDateTime(r.raw_artifact!.expires_at)}` : ''}`
                    : r.raw_artifact!.state === 'expired'
                      ? 'Aufbewahrungsfrist abgelaufen, Daten gelöscht'
                      : 'Datei fehlt'}
                </li>
              ))}
          </ul>
        </>
      )}
      {versions && versions.length > 0 && (
        <>
          <h4 className="mini-h">Alle Versionen dieser Karte</h4>
          <ol className="plain versions">
            {[...versions]
              .sort((a, b) => b.version - a.version)
              .map((ver) => (
                <li key={ver.id}>
                  {ver.id === v.id ? (
                    <strong aria-current="true">Version {ver.version} (angezeigt)</strong>
                  ) : (
                    <button type="button" className="text-button" onClick={(e) => openVersion(ver.id, e.currentTarget)}>
                      Version {ver.version}
                    </button>
                  )}{' '}
                  <span className="muted small">{formatDateTime(ver.created_at)}</span>
                </li>
              ))}
          </ol>
        </>
      )}
    </div>
  );
}

function RoleTag({ e }: { e: Evidence }) {
  const kind = recordKind(e, useSourceCatalog());
  if (kind === 'federal') return <span className="tag tag-role"> Mitteilung des Bundes</span>;
  if (kind === 'parliament') return <span className="tag tag-plain"> Parlamentsdaten</span>;
  return null;
}

/** Byline, type, issuer, procedure and provenance of one source item, where stated. */
function AttributionLines({ e, payload }: { e: Evidence; payload: NewsPayload }) {
  const kind = recordKind(e, useSourceCatalog());
  if (kind === 'parliament') return <ParliamentLines e={e} />;
  const a = e.attribution ?? {};
  const ps = payload.sources?.find((s) => s.url === e.url);
  const type = a.article_type ?? ps?.article_type ?? null;
  // The role tag already says "Mitteilung des Bundes"; the type label would repeat it.
  const typeLabel = type && type !== 'official_communication' ? ARTICLE_TYPE_LABELS[type] : undefined;
  const issuer = a.issuer ?? ps?.issuer ?? null;
  const stage = a.procedural_stage ?? ps?.procedural_stage ?? null;
  const lines: ReactNode[] = [];
  if (typeLabel || a.byline) {
    lines.push(
      <>
        {typeLabel && <strong>{typeLabel}</strong>}
        {typeLabel && a.byline ? ' · ' : ''}
        {a.byline && <>von {a.byline}</>}
      </>,
    );
  }
  if (issuer) lines.push(<>Herausgegeben von {issuer}{a.document_id ? ` · Dokument ${a.document_id}` : ''}</>);
  if (stage || a.affair_id || a.council) {
    lines.push(
      <>
        {stage && <>Verfahrensstand: {stage}</>}
        {a.council && <>{stage ? ' · ' : ''}{a.council}</>}
        {a.affair_id && <>{stage || a.council ? ' · ' : ''}Geschäft {a.affair_id}</>}
      </>,
    );
  }
  // A date without a time (or stated day precision) is a calendar date, never a midnight instant.
  if (a.event_date)
    lines.push(
      <>Datum des Ereignisses: {a.date_precision === 'day' || /^\d{4}-\d{2}-\d{2}$/.test(a.event_date) ? dayOnly(a.event_date) : formatDateTime(a.event_date)}</>,
    );
  if (a.discovered_via || a.original_publisher) {
    lines.push(
      <>
        {a.discovered_via && <>Gefunden über {a.discovered_via}</>}
        {a.discovered_via && a.original_publisher ? ' · ' : ''}
        {a.original_publisher && (
          <>
            ursprünglich erschienen bei{' '}
            {a.original_url ? (
              <a href={a.original_url} target="_blank" rel="noopener noreferrer">
                {a.original_publisher} ↗
              </a>
            ) : (
              a.original_publisher
            )}
          </>
        )}
      </>,
    );
  }
  if (a.republished_from) lines.push(<>Übernommen von {a.republished_from}</>);
  if (a.categories && a.categories.length > 0) lines.push(<>Rubrik: {a.categories.join(', ')}</>);
  return (
    <>
      {lines.map((l, i) => (
        <p className="attribution" key={i}>
          {l}
        </p>
      ))}
      {a.terms_attribution && <p className="terms">{a.terms_attribution}</p>}
    </>
  );
}

/** A parliament record: procedure facts first, then retrieval time and the required attribution. */
function ParliamentLines({ e }: { e: Evidence }) {
  const a = e.attribution ?? {};
  return (
    <>
      {(a.procedural_stage || a.council || a.event_date) && (
        <p className="record-facts">
          {a.procedural_stage && (
            <span>
              Verfahrensstand: <strong>{a.procedural_stage}</strong>
            </span>
          )}
          {a.council && (
            <span>
              Rat: <strong>{a.council}</strong>
            </span>
          )}
          {a.event_date && (
            <span>
              Datum: <strong>{dayOnly(a.event_date)}</strong>
            </span>
          )}
        </p>
      )}
      {a.affair_id && (
        <p className="attribution">
          Geschäft {a.affair_id}
          {a.document_id ? ` · Dokument ${a.document_id}` : ''}
        </p>
      )}
      {a.byline && <p className="attribution">von {a.byline}</p>}
      <p className="attribution">{dataRetrieval(e) ?? 'Datenbezug: Zeitpunkt nicht erfasst'}</p>
      {a.terms_attribution && <p className="terms">{a.terms_attribution}</p>}
    </>
  );
}

function CoverageDetails({
  payload,
  evidence,
  fallback,
}: {
  payload: NewsPayload;
  evidence: EvidenceView | null;
  fallback: CardVersion['evidence'];
}) {
  const raw = evidence?.evidence ?? fallback ?? [];
  const revisions = evidence?.revisions ?? [];
  const fetched = (id: string) => revisions.find((r) => r.revision_id === id)?.fetched_at;
  const groups = groupEvidence(raw, fetched).filter((g) => g.older.length > 0);
  const details = classifyUnknowns(payload).coverage.filter((c) => c.detail);
  const notes = revisions.filter((r) => r.completeness?.notes);
  if (groups.length === 0 && details.length === 0 && notes.length === 0) return null;
  return (
    <>
      {(details.length > 0 || notes.length > 0) && (
        <>
          <h4 className="mini-h">Erfassungsdiagnose</h4>
          <ul className="plain small">
            {details.map((c, i) => (
              <li key={`d-${i}`} lang="en">
                {c.detail}
              </li>
            ))}
            {notes.map((r) => (
              <li key={r.revision_id} lang="en">
                {r.source_id}: {r.completeness.notes}
              </li>
            ))}
          </ul>
        </>
      )}
      {groups.length > 0 && (
        <>
          <h4 className="mini-h">Frühere Abrufe derselben Artikel</h4>
          <ul className="plain small">
            {groups.map((g) => (
              <li key={g.latest.item_id || g.latest.revision_id}>
                {g.latest.source_name || g.latest.source_id}: {g.older.length} frühere {g.older.length === 1 ? 'Revision' : 'Revisionen'} (
                {g.older.map((o) => formatDateTime(fetched(o.revision_id) ?? null)).join(', ')})
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
